import {
  MetricsQualifierInterface,
  MetricsTypeInterface,
  QuantitativeDataOperator,
  QuantitativeDataQualifierInterface,
  QuantitativeDataTypeInterface
} from '../interfaces/criterions/metrics-type.interface';

/**
 * Données chiffrées : échelles des curseurs, formatage des valeurs selon l'unité du qualificatif
 * et lecture des données chiffrées d'une recherche (quantitativeData).
 */
export class MetricsUtils {

  private static readonly SLIDER_VALUES: { [units: string]: number[] } = {
    'm²': [0, 100, 250, 500, 1000, 2500, 5000, 10000, 25000, 50000, 100000, 1000000],
    'k€': [0, 10, 50, 100, 250, 500, 1000, 5000, 10000, 50000, 100000, 1000000],
    'M€': [0, 0.5, 1, 2, 5, 10, 20, 50, 100, 250, 500, 1000]
  };

  // Sans unité (ex. nombre de postes)
  private static readonly DEFAULT_SLIDER_VALUES: number[] = [0, 5, 10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000];

  public static getSliderValues(units: string): number[] {
    return MetricsUtils.SLIDER_VALUES[units] || MetricsUtils.DEFAULT_SLIDER_VALUES;
  }

  public static formatValue(value: number, units: string): string {
    const format = (v: number) => new Intl.NumberFormat('fr-FR').format(v);
    if (units === 'k€') {
      if (value >= 1000000) {
        return format(value / 1000000) + 'Mds€';
      }
      if (value >= 1000) {
        return format(value / 1000) + 'M€';
      }
      return format(value) + 'K€';
    }
    if (units === 'M€' && value >= 1000) {
      return format(value / 1000) + 'Mds€';
    }
    return format(value) + (units ? ' ' + units : '');
  }

  /**
   * Qualificatif du référentiel (nom, unité)
   */
  public static findQualifier(metricsTypes: Array<MetricsTypeInterface>, qualifierId: number): MetricsQualifierInterface {
    for (const type of metricsTypes || []) {
      const qualifier = (type.qualifiers || []).find(q => q.id === qualifierId);
      if (qualifier) {
        return qualifier;
      }
    }
    return undefined;
  }

  /**
   * Filtre enregistré dans une recherche pour un qualificatif (bornes, articles sans informations)
   */
  public static findSelectedQualifier(quantitativeData: Array<QuantitativeDataTypeInterface>, qualifierId: number): QuantitativeDataQualifierInterface {
    for (const type of quantitativeData || []) {
      const qualifier = (type.qualifiers || []).find(q => q.qualifierId === qualifierId);
      if (qualifier) {
        return qualifier;
      }
    }
    return undefined;
  }

  /**
   * Nombre de qualificatifs filtrés
   */
  public static count(quantitativeData: Array<QuantitativeDataTypeInterface>): number {
    return (quantitativeData || []).reduce((count, type) => count + (type.qualifiers || []).length, 0);
  }

  /**
   * Opérateur entre les qualificatifs d'un type : 'and' par défaut (absent = recherches enregistrées avant son ajout)
   */
  public static operator(type: QuantitativeDataTypeInterface): QuantitativeDataOperator {
    return type?.operator === 'or' ? 'or' : 'and';
  }

  /**
   * Paramètres de la recherche d'articles : metrics "idQualificatif;min;max" (borne absente = vide),
   * metricsNull (qualificatifs pour lesquels inclure les articles sans informations)
   * et metricsOr (types dont les qualificatifs se combinent en OU ; les autres en ET)
   */
  public static toQueryParams(quantitativeData: Array<QuantitativeDataTypeInterface>): { metrics: Array<string>, metricsNull: Array<number>, metricsOr: Array<number> } {
    const metrics: Array<string> = [];
    const metricsNull: Array<number> = [];
    const metricsOr: Array<number> = [];
    for (const type of quantitativeData || []) {
      if (MetricsUtils.operator(type) === 'or' && (type.qualifiers || []).length > 0) {
        metricsOr.push(type.typeId);
      }
      for (const qualifier of type.qualifiers || []) {
        metrics.push(qualifier.qualifierId + ';' + (qualifier.min ?? '') + ';' + (qualifier.max ?? ''));
        if (qualifier.includeNull) {
          metricsNull.push(qualifier.qualifierId);
        }
      }
    }
    return {metrics, metricsNull, metricsOr};
  }

  /**
   * Filtres d'une recherche avec leur libellé, ex. {qualifierId: 1, label: "Terrain : entre 500 m² et 5 000 m²"},
   * et l'opérateur qui les relie au filtre précédent (operatorBefore) : celui du type entre filtres d'un même type,
   * ET entre types, absent pour le premier filtre
   */
  public static items(metricsTypes: Array<MetricsTypeInterface>, quantitativeData: Array<QuantitativeDataTypeInterface>): Array<{ qualifierId: number, label: string, operatorBefore?: QuantitativeDataOperator }> {
    const items: Array<{ qualifierId: number, label: string, operatorBefore?: QuantitativeDataOperator }> = [];
    for (const type of quantitativeData || []) {
      let firstOfType = true;
      for (const selected of type.qualifiers || []) {
        const qualifier = MetricsUtils.findQualifier(metricsTypes, selected.qualifierId);
        if (qualifier) {
          items.push({
            qualifierId: selected.qualifierId,
            label: qualifier.name + ' : ' + MetricsUtils.rangeLabel(selected, qualifier.units)
              + (selected.includeNull ? ' (articles sans informations inclus)' : ''),
            operatorBefore: items.length === 0 ? undefined : (firstOfType ? 'and' : MetricsUtils.operator(type))
          });
          firstOfType = false;
        }
      }
    }
    return items;
  }

  /**
   * Phrase expliquant le résultat des filtres d'un type,
   * ex. "Articles avec terrain d'au moins 500 m² et locaux d'au moins 5 000 m²" (ou "… ou …" en OU)
   */
  public static sentence(metricsTypes: Array<MetricsTypeInterface>, type: QuantitativeDataTypeInterface): string {
    const parts: Array<string> = [];
    for (const selected of type?.qualifiers || []) {
      const qualifier = MetricsUtils.findQualifier(metricsTypes, selected.qualifierId);
      if (qualifier) {
        const range = MetricsUtils.rangeSentence(selected, qualifier.units);
        parts.push(qualifier.name.toLowerCase() + ' ' + range
          + (selected.includeNull ? (range === 'renseigné' ? ' ou non' : ' (ou non renseigné)') : ''));
      }
    }
    if (parts.length === 0) {
      return '';
    }
    return 'Articles avec ' + parts.join(MetricsUtils.operator(type) === 'or' ? ' ou ' : ' et ');
  }

  /**
   * Restriction des filtres d'une recherche aux qualificatifs cochés (filtre des résultats) : si au moins un qualificatif
   * est coché, seuls les cochés sont actifs, combinés selon l'opérateur de leur type (ET entre types)
   */
  public static restrictToSelection(quantitativeData: Array<QuantitativeDataTypeInterface>, selectedQualifierIds: Set<number>): Array<QuantitativeDataTypeInterface> {
    if (selectedQualifierIds.size === 0) {
      return quantitativeData || [];
    }
    return (quantitativeData || [])
      .map(type => ({typeId: type.typeId, operator: type.operator, qualifiers: (type.qualifiers || []).filter(qualifier => selectedQualifierIds.has(qualifier.qualifierId))}))
      .filter(type => type.qualifiers.length > 0);
  }

  /**
   * Libellés des filtres d'une recherche, ex. "Terrain : entre 500 m² et 5 000 m²"
   */
  public static labels(metricsTypes: Array<MetricsTypeInterface>, quantitativeData: Array<QuantitativeDataTypeInterface>): Array<string> {
    const labels: Array<string> = [];
    for (const type of quantitativeData || []) {
      for (const selected of type.qualifiers || []) {
        const qualifier = MetricsUtils.findQualifier(metricsTypes, selected.qualifierId);
        if (qualifier) {
          labels.push(qualifier.name + ' : ' + MetricsUtils.rangeLabel(selected, qualifier.units)
            + (selected.includeNull ? ' (articles sans informations inclus)' : ''));
        }
      }
    }
    return labels;
  }

  private static rangeLabel(selected: QuantitativeDataQualifierInterface, units: string): string {
    const hasMin = selected.min !== null && selected.min !== undefined;
    const hasMax = selected.max !== null && selected.max !== undefined;
    if (hasMin && hasMax) {
      return 'entre ' + MetricsUtils.formatValue(selected.min, units) + ' et ' + MetricsUtils.formatValue(selected.max, units);
    }
    if (hasMin) {
      return 'à partir de ' + MetricsUtils.formatValue(selected.min, units);
    }
    if (hasMax) {
      return 'jusqu\'à ' + MetricsUtils.formatValue(selected.max, units);
    }
    return 'toutes valeurs';
  }

  private static rangeSentence(selected: QuantitativeDataQualifierInterface, units: string): string {
    const hasMin = selected.min !== null && selected.min !== undefined;
    const hasMax = selected.max !== null && selected.max !== undefined;
    if (hasMin && hasMax) {
      return 'entre ' + MetricsUtils.formatValue(selected.min, units) + ' et ' + MetricsUtils.formatValue(selected.max, units);
    }
    if (hasMin) {
      return 'd\'au moins ' + MetricsUtils.formatValue(selected.min, units);
    }
    if (hasMax) {
      return 'jusqu\'à ' + MetricsUtils.formatValue(selected.max, units);
    }
    return 'renseigné';
  }
}
